import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { useSyncExternalStore } from 'react';
import { LANGUAGE_TAGS, formatDateLabel, isLanguage, localizedUrl, splitLocalePath, translateDisplay, type Messages, type StoreLanguage } from './core';

void i18n.use(initReactI18next).init({
  lng: 'es', fallbackLng: 'es', supportedLngs: ['es', 'en', 'de'],
  resources: { es: { translation: {} } }, initImmediate: false,
  keySeparator: false, nsSeparator: false, returnNull: false,
  interpolation: { escapeValue: false }, react: { useSuspense: false },
});

const loading = new Map<StoreLanguage, Promise<void>>();
let requestedLanguage = 0;
export async function loadLanguage(language: StoreLanguage): Promise<void> {
  if (language !== 'es' && !i18n.hasResourceBundle(language, 'translation')) {
    let request = loading.get(language);
    if (!request) {
      request = (language === 'en' ? import('./locales/en.json') : import('./locales/de.json'))
        .then(module => { i18n.addResourceBundle(language, 'translation', module.default, true, true); });
      loading.set(language, request);
      request.catch(() => loading.delete(language));
    }
    await request;
  }
}
export async function prepareLanguage(language: StoreLanguage): Promise<void> {
  const requestId = ++requestedLanguage;
  await loadLanguage(language);
  if (requestId === requestedLanguage) await i18n.changeLanguage(language);
}
export function currentLanguage(): StoreLanguage {
  const value = i18n.resolvedLanguage || i18n.language;
  return isLanguage(value) ? value : 'es';
}
export function currentMessages(): Messages { return i18n.getResourceBundle(currentLanguage(), 'translation') || {}; }
export function messagesForLanguage(language: StoreLanguage): Messages { return i18n.getResourceBundle(language, 'translation') || {}; }
function subscribeLanguage(notify: () => void) {
  i18n.on('languageChanged', notify);
  return () => { i18n.off('languageChanged', notify); };
}
export function useStoreLanguage(): StoreLanguage { return useSyncExternalStore(subscribeLanguage, currentLanguage, currentLanguage); }
export function tr<T>(value: T): T { return translateDisplay(value, currentMessages(), currentLanguage()); }
export function localeTag(): string { return LANGUAGE_TAGS[currentLanguage()]; }
export function localizedDate(value: string): string { return formatDateLabel(value, currentLanguage()); }
export function localeLink<T>(value: T): T { return (typeof value === 'string' ? localizedUrl(value, currentLanguage()) : value) as T; }
export function languageFromBrowserPath(): StoreLanguage { return splitLocalePath(window.location.pathname).language; }

export default i18n;
